import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


export async function exportExcelDistrictWise({ apiData, fileName }) {
    try {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('District Wise Summary', {
            views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
        });

        const hasShares = apiData.some(d =>
            d.productMaleFemaleSummaryDtoList?.some(p => p.productName === 'SHARES')
        );
        const hasDeposits = apiData.some(d =>
            d.productMaleFemaleSummaryDtoList?.some(p => p.productName === 'MEMBER DEPOSIT')
        );

        const columns = [
            { header: '', key: 'districtName', width: 25 },
            { header: '', key: 'branchCount', width: 15 },
        ];

        if (hasShares) {
            columns.push(
                { header: '', key: 'shareFemale', width: 15 },
                { header: '', key: 'shareMale', width: 15 },
                { header: '', key: 'shareTotal', width: 15 },
                { header: '', key: 'shareAmount', width: 20, style: { numFmt: '#,##0.00' } }
            );
        }

        if (hasDeposits) {
            columns.push(
                { header: '', key: 'depositFemale', width: 15 },
                { header: '', key: 'depositMale', width: 15 },
                { header: '', key: 'depositTotal', width: 15 },
                { header: '', key: 'depositAmount', width: 20, style: { numFmt: '#,##0.00' } }
            );
        }

        sheet.columns = columns;

        const headerRow1 = ['District', 'No of Branches'];
        const headerRow2 = ['', ''];
        if (hasShares) {
            headerRow1.push('Number of Shares', '', '', '');
            headerRow2.push('Female', 'Male', 'Total', 'Amount');
        }
        if (hasDeposits) {
            headerRow1.push('Number of Member Deposits', '', '', '');
            headerRow2.push('Female', 'Male', 'Total', 'Amount');
        }

        sheet.getRow(1).values = headerRow1;
        sheet.getRow(2).values = headerRow2;

        let mergeIndex = 3;
        if (hasShares) {
            sheet.mergeCells(1, mergeIndex, 1, mergeIndex + 3);
            mergeIndex += 4;
        }
        if (hasDeposits) {
            sheet.mergeCells(1, mergeIndex, 1, mergeIndex + 3);
        }

        const headerStyle = {
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
            font: { bold: true },
            alignment: { vertical: 'middle', horizontal: 'center' },
            border: {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            },
        };

        for (let i = 1; i <= 2; i++) {
            const row = sheet.getRow(i);
            row.height = 20;
            row.eachCell(cell => Object.assign(cell.style, headerStyle));
        }

        const firstDataRow = 3;
        for (const district of apiData) {
            const shares = district.productMaleFemaleSummaryDtoList.find(p => p.productName === 'SHARES') || {};
            const deposits = district.productMaleFemaleSummaryDtoList.find(p => p.productName === 'MEMBER DEPOSIT') || {};

            const baseRow = [district.districtName, district.branchCount];

            if (hasShares) {
                baseRow.push(
                    shares.femaleCount || 0,
                    shares.maleCount || 0,
                    null,
                    shares.amount || 0
                );
            }

            if (hasDeposits) {
                baseRow.push(
                    deposits.femaleCount || 0,
                    deposits.maleCount || 0,
                    null,
                    deposits.amount || 0
                );
            }

            const row = sheet.addRow(baseRow);

            let colIndex = 3;
            if (hasShares) {
                const fCol = getExcelCol(colIndex);
                const mCol = getExcelCol(colIndex + 1);
                row.getCell(colIndex + 2).value = { formula: `${fCol}${row.number}+${mCol}${row.number}` };
                colIndex += 4;
            }
            if (hasDeposits) {
                const fCol = getExcelCol(colIndex);
                const mCol = getExcelCol(colIndex + 1);
                row.getCell(colIndex + 2).value = { formula: `${fCol}${row.number}+${mCol}${row.number}` };
            }

            row.eachCell(cell => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };
            });
        }

        const dataRowCount = sheet.rowCount;
        const totals = ['GRAND TOTAL', { formula: `SUBTOTAL(9,B${firstDataRow}:B${dataRowCount})` }];

        let colPos = 3;
        const totalCols = (hasShares ? 4 : 0) + (hasDeposits ? 4 : 0);
        for (let i = 0; i < totalCols; i++) {
            const colLetter = getExcelCol(colPos);
            totals.push({ formula: `SUBTOTAL(9,${colLetter}${firstDataRow}:${colLetter}${dataRowCount})` });
            colPos++;
        }

        const totalRow = sheet.addRow(totals);
        totalRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    } catch (error) {
        console.error('Excel generation failed:', error);
        throw error;
    }
}

export async function exportExcelBranchWise({ apiData, fileName }) {
    try {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Branch Wise Summary', {
            views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
        });

        const hasShares = apiData.some(branch =>
            branch.productMaleFemaleSummaryDtoList?.some(p => p.productName === 'SHARES')
        );
        const hasDeposits = apiData.some(branch =>
            branch.productMaleFemaleSummaryDtoList?.some(p => p.productName === 'MEMBER DEPOSIT')
        );

        const columns = [{ header: '', key: 'branchName', width: 25 }];
        if (hasShares) {
            columns.push(
                { header: '', key: 'shareFemaleCount', width: 15 },
                { header: '', key: 'shareMaleCount', width: 15 },
                { header: '', key: 'shareTotalCount', width: 15 },
                { header: '', key: 'shareAmount', width: 20, style: { numFmt: '#,##0.00' } }
            );
        }
        if (hasDeposits) {
            columns.push(
                { header: '', key: 'depositFemaleCount', width: 15 },
                { header: '', key: 'depositMaleCount', width: 15 },
                { header: '', key: 'depositTotalCount', width: 15 },
                { header: '', key: 'depositAmount', width: 20, style: { numFmt: '#,##0.00' } }
            );
        }

        sheet.columns = columns;

        const headerRow1 = ['Branch Name'];
        const headerRow2 = [''];
        if (hasShares) {
            headerRow1.push('Number of Shares', '', '', '');
            headerRow2.push('Female', 'Male', 'Total', 'Amount');
        }
        if (hasDeposits) {
            headerRow1.push('Number of Member Deposits', '', '', '');
            headerRow2.push('Female', 'Male', 'Total', 'Amount');
        }

        sheet.getRow(1).values = headerRow1;
        sheet.getRow(2).values = headerRow2;

        let mergeIndex = 2;
        if (hasShares) {
            sheet.mergeCells(1, mergeIndex, 1, mergeIndex + 3);
            mergeIndex += 4;
        }
        if (hasDeposits) {
            sheet.mergeCells(1, mergeIndex, 1, mergeIndex + 3);
        }

        const headerStyle = {
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
            font: { bold: true },
            alignment: { vertical: 'middle', horizontal: 'center' },
            border: {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            },
        };

        for (let i = 1; i <= 2; i++) {
            const row = sheet.getRow(i);
            row.height = 20;
            row.eachCell(cell => Object.assign(cell.style, headerStyle));
        }

        const firstDataRow = 3;
        for (const branch of apiData) {
            const shares = branch.productMaleFemaleSummaryDtoList?.find(p => p.productName === 'SHARES') || {};
            const deposits = branch.productMaleFemaleSummaryDtoList?.find(p => p.productName === 'MEMBER DEPOSIT') || {};

            const baseRow = [branch.branchName];

            if (hasShares) {
                baseRow.push(
                    shares.femaleCount || 0,
                    shares.maleCount || 0,
                    null,
                    shares.amount || 0
                );
            }

            if (hasDeposits) {
                baseRow.push(
                    deposits.femaleCount || 0,
                    deposits.maleCount || 0,
                    null,
                    deposits.amount || 0
                );
            }

            const row = sheet.addRow(baseRow);

            let colIndex = 2;
            if (hasShares) {
                const fCol = getExcelCol(colIndex);
                const mCol = getExcelCol(colIndex + 1);
                row.getCell(colIndex + 2).value = { formula: `${fCol}${row.number}+${mCol}${row.number}` };
                colIndex += 4;
            }
            if (hasDeposits) {
                const fCol = getExcelCol(colIndex);
                const mCol = getExcelCol(colIndex + 1);
                row.getCell(colIndex + 2).value = { formula: `${fCol}${row.number}+${mCol}${row.number}` };
            }

            row.eachCell(cell => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };
            });
        }

        const dataRowCount = sheet.rowCount;
        const totals = ['GRAND TOTAL'];

        let colPos = 2;
        const totalCols = (hasShares ? 4 : 0) + (hasDeposits ? 4 : 0);
        for (let i = 0; i < totalCols; i++) {
            const colLetter = getExcelCol(colPos);
            totals.push({ formula: `SUBTOTAL(9,${colLetter}${firstDataRow}:${colLetter}${dataRowCount})` });
            colPos++;
        }

        const totalRow = sheet.addRow(totals);
        totalRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    } catch (error) {
        console.error('Excel generation failed:', error);
        throw error;
    }
}



function getExcelCol(colIndex) {
    let col = '';
    while (colIndex > 0) {
        let rem = (colIndex - 1) % 26;
        col = String.fromCharCode(65 + rem) + col;
        colIndex = Math.floor((colIndex - 1) / 26);
    }
    return col;
}






